import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "../styles/Signupmodal.css";

const SignUpModal = ({ onClose, onSignupSuccess }) => {
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [emailSuggestion, setEmailSuggestion] = useState(null);
    const [usernameSuggestion, setUsernameSuggestion] = useState(null);
    const [emailValid, setEmailValid] = useState(true);
    const [isChecking, setIsChecking] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const modalRef = useRef(null);
    const debounceTimer = useRef(null);

    const validateEmail = (value) => {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return regex.test(value);
    };

    const generateRandomSuffix = () => Math.floor(Math.random() * 900) + 100;

    const checkAvailability = useCallback(async (currentEmail, currentUsername) => {
        if (!currentEmail && !currentUsername) {
            setEmailSuggestion(null);
            setUsernameSuggestion(null);
            return;
        }

        setIsChecking(true);
        try {
            // Check username availability
            if (currentUsername && currentUsername.length >= 3) {
                const usernameResponse = await axios.get(
                    `${process.env.REACT_APP_AUTH_BASE_URL}/check-username/${currentUsername}`,
                    { validateStatus: (status) => status < 500 }
                );

                if (usernameResponse.status === 200 && !usernameResponse.data.isAvailable) {
                    setUsernameSuggestion(`${currentUsername}${generateRandomSuffix()}`);
                } else {
                    setUsernameSuggestion(null);
                }
            }

            // Check email availability
            if (currentEmail && validateEmail(currentEmail)) {
                const emailResponse = await axios.get(
                    `${process.env.REACT_APP_AUTH_BASE_URL}/check-email/${currentEmail}`,
                    { validateStatus: (status) => status < 500 }
                );

                if (emailResponse.status === 200 && !emailResponse.data.isAvailable) {
                    const atIndex = currentEmail.indexOf("@");
                    const name = currentEmail.substring(0, atIndex);
                    const domain = currentEmail.substring(atIndex);
                    setEmailSuggestion(`${name}${generateRandomSuffix()}${domain}`);
                } else {
                    setEmailSuggestion(null);
                }
            }
        } catch (error) {
            console.error("Availability check error:", error);
            // Fallback suggestions
            if (currentEmail.includes("@")) {
                const atIndex = currentEmail.indexOf("@");
                const name = currentEmail.substring(0, atIndex);
                const domain = currentEmail.substring(atIndex);
                setEmailSuggestion(`${name}${generateRandomSuffix()}${domain}`);
            }
            if (currentUsername) {
                setUsernameSuggestion(`${currentUsername}${generateRandomSuffix()}`);
            }
        } finally {
            setIsChecking(false);
        }
    }, []);

    // Debounce the availability checks
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            checkAvailability(email, username);
        }, 500);
        return () => clearTimeout(debounceTimer.current);
    }, [email, username, checkAvailability]);

    // Clear suggestions when fields are empty
    useEffect(() => {
        if (!email) setEmailSuggestion(null);
        if (!username) setUsernameSuggestion(null);
    }, [email, username]);

    // Handle click outside modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // Adjust modal height based on suggestions
    useEffect(() => {
        const modal = modalRef.current;
        if (!modal) return;

        const isMobile = window.innerWidth <= 768;
        const hasEmailSuggestion = !!emailSuggestion;
        const hasUsernameSuggestion = !!usernameSuggestion;

        if (!isMobile) {
            modal.style.height = "30%";
        }

        if (hasEmailSuggestion && hasUsernameSuggestion) {
            modal.style.height = isMobile ? "330px" : "345px";
        } else if (hasEmailSuggestion || hasUsernameSuggestion) {
            modal.style.height = isMobile ? "300px" : "320px";
        } else {
            if (!isMobile) {
                modal.style.height = "288px";
            } else {
                modal.style.height = "33%";
            }
        }
    }, [emailSuggestion, usernameSuggestion]);

    const handleSignup = async () => {
        if (!validateEmail(email)) {
            setEmailValid(false);
            setMessage("Please enter a valid email address");
            return;
        }

        if (!username || username.length < 3) {
            setMessage("Username must be at least 3 characters");
            return;
        }

        setIsSubmitting(true);
        setMessage("Creating your account...");
        setMessageType("info");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_AUTH_BASE_URL}/register`,
                { email, username },
                { 
                    headers: { "Content-Type": "application/json" },
                    validateStatus: (status) => status < 500
                }
            );

            if (response.data.token) {
                localStorage.setItem("token", response.data.token);
                setMessage("Account created successfully!");
                setMessageType("success");
                
                setTimeout(() => {
                    onClose();
                    onSignupSuccess();
                    window.location.reload();
                }, 1500);
            } else if (response.data.errors) {
                setMessage(response.data.errors.join('\n'));
                setMessageType("error");
            }
        } catch (error) {
            console.error("Signup Error:", error);
            
            if (error.response) {
                // Server responded with error status
                const serverError = error.response.data?.errors?.join('\n') || 
                                  error.response.data?.error || 
                                  "Signup failed. Please try again.";
                setMessage(serverError);
            } else if (error.request) {
                // Request was made but no response
                setMessage("Network error - please try again");
            } else {
                // Other errors
                setMessage("Signup failed - please try again");
            }
            setMessageType("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="signup-modal-content" ref={modalRef}>
                <div className="navbar-logo-container">
                    <p></p>
                </div>

                <div className="modal-body">
                    <div className="input-group">
                        <input
                            type="text"
                            placeholder="Choose a username (min 3 chars)"
                            value={username}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\s/g, "");
                                setUsername(value);
                                setMessage("");
                            }}
                            maxLength={20}
                            onKeyPress={(e) => e.key === 'Enter' && handleSignup()}
                        />
                        {usernameSuggestion && (
                            <div className="suggestion-text">
                                <span>Username taken. Try: </span>
                                <span
                                    className="suggestion-link"
                                    onClick={() => {
                                        setUsername(usernameSuggestion);
                                        setMessage("");
                                    }}
                                >
                                    {usernameSuggestion}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="input-group">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => {
                                const cleanValue = e.target.value.replace(/\s/g, "");
                                setEmail(cleanValue);
                                setEmailValid(validateEmail(cleanValue));
                                setMessage("");
                            }}
                            className={emailValid ? "" : "input-error"}
                            onKeyPress={(e) => e.key === 'Enter' && handleSignup()}
                        />
                        {emailSuggestion && (
                            <div className="suggestion-text">
                                <span>Email already registered. Try: </span>
                                <span
                                    className="suggestion-link"
                                    onClick={() => {
                                        setEmail(emailSuggestion);
                                        setEmailValid(true);
                                        setMessage("");
                                    }}
                                >
                                    {emailSuggestion}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="login-link-container">
                        <span>Already have an account? </span>
                        <span 
                            className="login-link" 
                            onClick={() => { 
                                onClose(); 
                                onSignupSuccess(); 
                            }}
                        >
                            Login
                        </span>
                    </div>

                    <button 
                        onClick={handleSignup} 
                        disabled={isChecking || isSubmitting}
                    >
                        {isSubmitting ? "Creating Account..." : "Sign Up"}
                    </button>

                    <p className={`message ${messageType || "info"}`}>
                        {message}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignUpModal;
